import { apiUrl } from './api-base';

/** Mismos MIME y tamaño que `ENUM_OBJECTS` en la app móvil. */
export const BUSINESS_PRESENTATION_IMAGE_TYPES = [
  'image/png',
  'image/jpg',
  'image/jpeg',
] as const;

export const BUSINESS_PRESENTATION_MAX_BYTES = 19_000_000;

export type BusinessTypeOption = {
  id: number;
  name: string;
};

export type BusinessRegisterPayload = {
  name: string;
  nit: string;
  telephone: string;
  location: string;
  password: string;
  description: string;
  lat: string | null;
  lon: string | null;
  acceptTerms: boolean;
  acceptDataPolicy: boolean;
};

/** GET `services/typeBussines` — catálogo usado en el registro móvil. */
export async function fetchBusinessTypes(): Promise<BusinessTypeOption[]> {
  const res = await fetch(apiUrl('services', 'typeBussines'), {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    return [];
  }
  const raw = (await res.json()) as unknown;
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: BusinessTypeOption[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) {
      continue;
    }
    const o = item as Record<string, unknown>;
    const id = typeof o.id === 'number' ? o.id : Number(o.id);
    const name = typeof o.name === 'string' ? o.name.trim() : '';
    if (Number.isFinite(id) && name) {
      out.push({ id, name });
    }
  }
  return out;
}

function buildRegisterDataJson(
  payload: BusinessRegisterPayload,
  typeBusiness: readonly BusinessTypeOption[]
): string {
  const body: Record<string, unknown> = {
    name: payload.name.trim(),
    nit: payload.nit.trim(),
    telephone: payload.telephone.trim(),
    location: payload.location.trim(),
    password: payload.password,
    description: payload.description.trim(),
    lat: payload.lat,
    lon: payload.lon,
    whatsapp: null,
    registrationEmail: '',
    is24Hours: true,
    isSevenDate: true,
    acceptTerms: payload.acceptTerms,
    acceptDataPolicy: payload.acceptDataPolicy,
    isWebAdmin: true,
    typeBusiness: typeBusiness.map((t) => ({ id: t.id })),
  };
  return JSON.stringify(body);
}

/**
 * POST `services/business` — multipart igual que la app móvil (`data` + `imagen`).
 */
export async function registerBusiness(
  payload: BusinessRegisterPayload,
  image: File,
  typeBusiness: readonly BusinessTypeOption[]
): Promise<{ ok: true } | { ok: false; message: string }> {
  const formData = new FormData();
  formData.append('data', buildRegisterDataJson(payload, typeBusiness));
  formData.append('imagen', image, image.name);

  let res: Response;
  try {
    res = await fetch(apiUrl('services', 'business'), {
      method: 'POST',
      body: formData,
    });
  } catch {
    return { ok: false, message: 'No se pudo conectar con el servidor. Intenta de nuevo.' };
  }

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text || null;
  }

  if (!res.ok) {
    const msg =
      typeof data === 'object' &&
      data !== null &&
      'message' in data &&
      typeof (data as { message: unknown }).message === 'string'
        ? (data as { message: string }).message
        : typeof data === 'string' && data.trim()
          ? data.trim()
          : 'No se pudo registrar la empresa. Verifica los datos e intenta de nuevo.';
    return { ok: false, message: msg };
  }

  return { ok: true };
}

export function validatePresentationImage(file: File): string | null {
  if (!BUSINESS_PRESENTATION_IMAGE_TYPES.includes(file.type as (typeof BUSINESS_PRESENTATION_IMAGE_TYPES)[number])) {
    return 'La imagen debe ser PNG o JPG.';
  }
  if (file.size > BUSINESS_PRESENTATION_MAX_BYTES) {
    return 'La imagen supera el tamaño máximo permitido.';
  }
  return null;
}
